<template>
  <NcrmRecordEditDialog
    :model-value="modelValue"
    :title="drawerTitle"
    :subtitle="$t('ncrm.workflows.editor.subtitle')"
    :save-label="saveLabel"
    :save-disabled="!canSave"
    :extra-action-label="deleteActionLabel"
    extra-action-color="error"
    :max-width="560"
    @update:model-value="$emit('update:modelValue', $event)"
    @save="handleSave"
    @extra-action="openDeleteDialog"
  >
    <div class="d-flex flex-column ga-4">
      <v-select
        v-model="form.templateKey"
        :items="templates"
        item-title="name"
        item-value="key"
        :label="$t('ncrm.workflows.editor.template')"
        hide-details="auto"
        :menu-props="selectMenuProps"
        :disabled="mode === 'edit'"
      />
      <v-text-field
        v-model="form.name"
        :label="$t('ncrm.workflows.editor.name')"
        hide-details="auto"
        autofocus
      />
      <v-select
        v-if="mode === 'edit'"
        v-model="form.status"
        :items="workflowStatusOptions"
        item-title="label"
        item-value="value"
        :label="$t('ncrm.workflows.editor.status')"
        hide-details="auto"
        :menu-props="selectMenuProps"
      />
      <v-alert type="info">
        {{ $t("ncrm.workflows.editor.nextStepHint") }}
      </v-alert>
    </div>
  </NcrmRecordEditDialog>

  <v-dialog v-model="deleteDialog" max-width="460">
    <v-card>
      <v-card-title class="d-flex align-center ga-2">
        <v-icon color="error">mdi-delete-outline</v-icon>
        {{ $t("ncrm.workflows.editor.deleteTitle") }}
      </v-card-title>
      <v-card-text>
        <v-alert type="warning" class="mb-4">
          {{ $t("ncrm.workflows.editor.deleteHint") }}
        </v-alert>
        <v-text-field
          v-model="deleteConfirmText"
          :label="$t('ncrm.workflows.editor.deleteConfirmLabel')"
          hide-details="auto"
          autofocus
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="closeDeleteDialog">
          {{ $t("app.actions.cancel") }}
        </v-btn>
        <v-btn color="error" :disabled="deleteSubmitDisabled" @click="confirmDelete">
          {{ $t("app.actions.delete") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import NcrmRecordEditDialog from "@/components/ncrm/shared/NcrmRecordEditDialog.vue";
import { WORKFLOW_TEMPLATE_KEYS } from "@/Utile/ncrmWorkflows.mjs";

const DELETE_CONFIRM_TOKEN = "DELETE";
const WORKFLOW_STATUSES = ["draft", "active", "paused"];

export default {
  name: "NcrmWorkflowEditorDrawer",
  components: {
    NcrmRecordEditDialog,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: "create",
    },
    workflow: {
      type: Object,
      default: null,
    },
    templates: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["update:modelValue", "save", "delete"],
  data() {
    return {
      form: {
        name: "",
        templateKey: WORKFLOW_TEMPLATE_KEYS.ENRICHMENT_OF_DATA,
        status: "draft",
      },
      deleteDialog: false,
      deleteConfirmText: "",
    };
  },
  computed: {
    drawerTitle() {
      return this.mode === "edit"
        ? this.$t("ncrm.workflows.editor.editTitle")
        : this.$t("ncrm.workflows.editor.createTitle");
    },
    saveLabel() {
      return this.mode === "edit" ? this.$t("app.actions.save") : this.$t("app.actions.create");
    },
    deleteActionLabel() {
      return this.mode === "edit" ? this.$t("ncrm.workflows.actions.deleteWorkflow") : "";
    },
    canSave() {
      const statusIsValid = this.mode !== "edit" || WORKFLOW_STATUSES.includes(this.form.status);
      return Boolean(String(this.form.name || "").trim() && this.form.templateKey && statusIsValid);
    },
    workflowStatusOptions() {
      return WORKFLOW_STATUSES.map((status) => ({
        value: status,
        label: this.$t(`ncrm.workflows.editor.statuses.${status}`),
      }));
    },
    selectMenuProps() {
      return {
        maxHeight: 320,
        zIndex: 2710,
      };
    },
    deleteSubmitDisabled() {
      return String(this.deleteConfirmText || "").trim().toUpperCase() !== DELETE_CONFIRM_TOKEN;
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(isOpen) {
        if (isOpen) this.resetForm();
      },
    },
    workflow: {
      deep: true,
      handler() {
        if (this.modelValue) this.resetForm();
      },
    },
    "form.templateKey"(next, previous) {
      if (this.mode !== "create") return;
      const current = String(this.form.name || "").trim();
      if (!current || current === this.defaultNameForTemplate(previous)) {
        this.form.name = this.defaultNameForTemplate(next);
      }
    },
  },
  methods: {
    defaultNameForTemplate(templateKey) {
      if (templateKey === WORKFLOW_TEMPLATE_KEYS.CONTACT_NORMALIZATION) {
        return this.$t("ncrm.workflows.templates.contactNormalization.defaultInstanceName");
      }
      return this.$t("ncrm.workflows.templates.enrichment.defaultInstanceName");
    },
    resetForm() {
      this.form = {
        name: this.workflow?.name || this.defaultNameForTemplate(this.workflow?.templateKey || WORKFLOW_TEMPLATE_KEYS.ENRICHMENT_OF_DATA),
        templateKey: this.workflow?.templateKey || WORKFLOW_TEMPLATE_KEYS.ENRICHMENT_OF_DATA,
        status: WORKFLOW_STATUSES.includes(this.workflow?.status) ? this.workflow.status : "draft",
      };
      this.closeDeleteDialog();
    },
    handleSave() {
      if (!this.canSave) return;
      const payload = {
        name: String(this.form.name || "").trim(),
        templateKey: this.form.templateKey,
      };
      if (this.mode === "edit") payload.status = this.form.status;
      this.$emit("save", payload);
    },
    openDeleteDialog() {
      if (this.mode !== "edit" || !this.workflow?.id) return;
      this.deleteConfirmText = "";
      this.deleteDialog = true;
    },
    closeDeleteDialog() {
      this.deleteDialog = false;
      this.deleteConfirmText = "";
    },
    confirmDelete() {
      if (this.deleteSubmitDisabled || !this.workflow?.id) return;
      this.$emit("delete", this.workflow);
      this.closeDeleteDialog();
    },
  },
};
</script>
